import React, { ReactElement, useState } from 'react';
import { Dropdown, DropdownButtonProps } from 'react-bootstrap';
import styled from 'styled-components';
import { Theme, Viewport } from '@livecontrol/core-ui';
import { Str } from '@livecontrol/core-utils';

const FilterDropwdown = styled.div`
  position: relative;
  button.btn-outline-tertiary {
    color: #0d1438;
    border-color: #8798ad;
  }

  button.btn-outline-tertiary:hover {
    border-color: #8798ad;
  }

  button.btn-outline-tertiary:first-letter {
    text-transform: uppercase;
  }

  .show > button.btn-outline-tertiary:hover {
    color: #fff !important;
    background-color: #8798ad;
  }
  .dropdown-menu.show {
    background-color: white;
    margin: 0 !important;
  }
  .dropdown-menu {
    margin: 0 !important;
  }

  button.dropdown-toggle {
    position: relative;
    padding: 8px 11px 8px 37px;
    color: #0d1438;
    font-size: 12px;
    font-weight: 600;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    margin: 0 !important;
    width: 210px;
  }

  button.dropdown-toggle:after {
    display: none;
  }

  .icon {
    position: absolute;
    width: 20px;
    height: 20px;
    top: 8px;
    left: 11px;
    background-repeat: no-repeat;
    background-size: cover;
    background-position: center center;
    margin-right: 6px;
  }

  .filter {
    background-image: url(/assets/icons/Filter-black.svg);
  }

  .item-name:first-letter {
    text-transform: uppercase;
  }

  @media (max-width: 540px) {
    button.dropdown-toggle {
      position: relative;
      padding: 8px 11px 8px 37px;
      color: #0d1438;
      font-size: 12px;
      font-weight: 600;
      display: flex;
      align-items: center;
      justify-content: flex-start;
      margin: 0 !important;
      width: fit-content;
    }
  }
`;

// Tipos de eventos disponibles
export const ASSET_FILTER_TYPES = [
  'Produced',
  'Static', 
  'Simulated Live',
  'Mobile',
  'Test',
  'External Encoder',
  'Clip' 
] as const;

export type AssetFilterType = typeof ASSET_FILTER_TYPES[number];

export const EventTypeFilter = (props: EventTypeFilter.Props): ReactElement => {
  const viewport = Theme.useViewport();
  const [isOpen, setIsOpen] = useState(false);

  const {
    value: { selected, available },
    onChange,
    ...rest
  } = props;

  let title = 
    !selected && viewport === Viewport.XS
      ? 'Asset Type'
      : selected ?? 'All Asset Types';

  if (available.length < 1) {
    title = 'No available assset types';
  } else if (available.length < 2) {
    title = available[0];
  }

  return (
    <FilterDropwdown>
      <Dropdown
        show={isOpen}
        onBlur={(): void => {
          setIsOpen(false);
        }}
        {...rest}
      >
        <Dropdown.Toggle
          variant={selected ? 'tint' : 'outline-tertiary'}
          disabled={available.length < 1}
          onClick={(): void => {
            setIsOpen(!isOpen);
          }}
          style={{ textTransform: 'capitalize' }}
        >
          <div className='icon filter' />
          {Str.truncate(title, { length: 30 })}
        </Dropdown.Toggle>

        <Dropdown.Menu
          onMouseDown={(e: React.SyntheticEvent): void => {
            e.preventDefault();
          }}
        >
          <Dropdown.Item
            key='all'
            onClick={(): void => {
              onChange?.(undefined);
              setIsOpen(false);
            }}
          >
            All Asset Types
          </Dropdown.Item>

          {[...available]
            .sort((a, b) => a.localeCompare(b))
            .map((eventType: AssetFilterType): ReactElement => {
              const active = selected === eventType;

              return (
                <Dropdown.Item
                  key={eventType}
                  active={active}
                  onClick={(): void => {
                    onChange?.(!active ? eventType : undefined);
                    setIsOpen(false);
                  }}
                >
                  <div className='item-name'>{eventType}</div>
                </Dropdown.Item>
              );
            })}
        </Dropdown.Menu>
      </Dropdown>
    </FilterDropwdown>
  );
};

// eslint-disable-next-line @typescript-eslint/no-redeclare
export namespace EventTypeFilter {
  export interface Value {
    available: readonly AssetFilterType[];
    selected?: AssetFilterType;
  }

  export type Props = Omit<DropdownButtonProps, 'onChange' | 'title'> & {
    value: Value;
    onChange?: (value?: AssetFilterType) => void;
  };
}